import React, { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  Brain,
  Camera,
  FastForward,
  Heart,
  CheckCircle2,
  XCircle,
  Phone,
  FileDown,
  Printer,
  CalendarDays,
  ShieldCheck,
  ArrowRight,
  Maximize2,
  X,
  Clock,
  Home,
  Check,
} from "lucide-react";
import {
  RelatedPages,
  allTreatmentsLinks,
  allServiceLinks,
  allDecisionLinks,
} from "@/components/RelatedPages";

export const AMPA_FLYER_IMAGE =
  "https://vibe.filesafe.space/1787327131284421342/attachments/9ef86b09-2880-4adf-a74c-b7e595d55846.png";

export const OneDayTmsPage: React.FC = () => {
  const [flyerModalOpen, setFlyerModalOpen] = useState(false);

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      window.print();
      return;
    }
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Ampa One - Neuro Wellness TMS Centers of America</title>
          <style>
            @page { size: auto; margin: 10mm; }
            body { margin: 0; display: flex; justify-content: center; align-items: center; background: #fff; font-family: sans-serif; }
            img { max-width: 100%; height: auto; display: block; }
          </style>
        </head>
        <body>
          <img src="${AMPA_FLYER_IMAGE}" alt="Ampa One Patient Educational Flyer" onload="window.print(); window.close();" />
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Ampa One™ TMS in Florida | Camera-Guided, Accelerated Depression Treatment | Coral Springs"
        description="Ampa One™ is FDA-cleared, camera-guided TMS for depression that hasn't improved with medication. Accelerated schedules in Coral Springs, FL. Free consultation: 954-827-9793."
        canonical="/ampa"
        keywords="Ampa One TMS, camera-guided TMS, accelerated TMS, treatment-resistant depression, Neuro Wellness Coral Springs, lightweight coil TMS, outpatient psychiatric care"
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "MedicalTherapy",
            name: "Ampa One™ TMS Therapy",
            description:
              "FDA-cleared, camera-guided transcranial magnetic stimulation (TMS) system for major depressive disorder that hasn't improved on antidepressant medication, offering accelerated treatment schedules.",
            url: "https://neurowellnesstms.com/ampa",
            procedureType: "Transcranial Magnetic Stimulation",
            relevantSpecialty: "Psychiatry",
            provider: {
              "@id": "https://neurowellnesstms.com/#organization",
            },
          },
        ]}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* HERO SECTION — Matching Flyer Headline & Theme */}
        <section className="relative overflow-hidden bg-gradient-navy text-primary-foreground py-16 sm:py-24 lg:py-28">
          <div className="pointer-events-none absolute -right-32 -top-24 h-96 w-96 rounded-full bg-accent/15 blur-3xl" />
          <div className="pointer-events-none absolute -left-40 bottom-0 h-80 w-80 rounded-full bg-medium-blue/20 blur-3xl" />

          <div className="container-px mx-auto max-w-4xl text-center px-4">
            <div className="space-y-6 animate-fade-up">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-accent/30 bg-accent/10 px-3.5 py-1 text-xs font-semibold text-accent uppercase tracking-wider">
                  <Sparkles className="h-3.5 w-3.5" />
                  Advanced Care · Real Hope
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs font-medium text-white/90 backdrop-blur-sm">
                  <ShieldCheck className="h-3.5 w-3.5 text-accent" />
                  FDA-Cleared for Depression
                </span>
              </div>

              <h1 className="font-headings text-3xl font-extrabold leading-[1.12] text-white sm:text-4xl md:text-5xl lg:text-[3.25rem]">
                Ampa One™ TMS in Florida: Camera-Guided,{" "}
                <span className="block text-gradient-accent">
                  Accelerated Depression Treatment
                </span>
              </h1>

              <p className="text-base sm:text-lg leading-relaxed text-white/90 max-w-2xl">
                Ampa One is an FDA-cleared transcranial magnetic stimulation
                (TMS) system for adults with major depression that hasn't
                improved on antidepressants. It uses a lightweight coil with a
                built-in camera and a pre-printed cap to keep every session on
                target, with sessions that take minutes instead of the 19–37
                minutes of conventional TMS. Neuro Wellness TMS Centers of
                America offers Ampa One in Coral Springs, serving all of South
                Florida.
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button
                  asChild
                  size="lg"
                  className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90 font-bold"
                >
                  <a href="/#consultation">Request a Free Consultation</a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/50 bg-white/5 text-white hover:bg-white/15 hover:border-white/70 font-semibold"
                >
                  <a href="tel:9548279793">
                    <Phone className="mr-2 h-4 w-4" />
                    Call 954-827-9793
                  </a>
                </Button>
                <Button
                  type="button"
                  size="lg"
                  variant="ghost"
                  onClick={() => setFlyerModalOpen(true)}
                  className="bg-white/10 text-white hover:bg-white/20 border border-white/20 font-semibold"
                >
                  <FileDown className="mr-2 h-4 w-4 text-accent" />
                  View &amp; Print Flyer
                </Button>
              </div>

              {/* Trust Pills */}
              <div className="pt-2 flex flex-wrap gap-y-2 gap-x-5 text-xs sm:text-sm text-white/80">
                <span className="flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-accent" /> Private Outpatient
                  Care
                </span>
                <span className="flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-accent" /> Weekend Availability
                </span>
                <span className="flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-accent" /> Dedicated Care Team
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* 4 CORE ADVANTAGES — Exactly matching the Flyer Row */}
        <section className="py-12 sm:py-16 bg-card border-b border-border">
          <div className="container-px mx-auto max-w-6xl px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {/* Pillar 1 */}
              <div className="rounded-2xl border border-border bg-background p-5 sm:p-6 text-center shadow-soft flex flex-col items-center">
                <div className="h-14 w-14 rounded-2xl bg-accent/10 text-accent flex items-center justify-center mb-3">
                  <Brain className="h-7 w-7" strokeWidth={2} />
                </div>
                <h3 className="font-headings text-sm sm:text-base font-bold text-primary uppercase tracking-wide">
                  FDA-Cleared
                </h3>
                <p className="mt-1 text-xs sm:text-sm font-semibold text-muted-foreground">
                  For Major Depression
                </p>
              </div>

              {/* Pillar 2 */}
              <div className="rounded-2xl border border-border bg-background p-5 sm:p-6 text-center shadow-soft flex flex-col items-center">
                <div className="h-14 w-14 rounded-2xl bg-accent/10 text-accent flex items-center justify-center mb-3">
                  <Camera className="h-7 w-7" strokeWidth={2} />
                </div>
                <h3 className="font-headings text-sm sm:text-base font-bold text-primary uppercase tracking-wide">
                  Camera-Guided
                </h3>
                <p className="mt-1 text-xs sm:text-sm font-semibold text-muted-foreground">
                  Every Session
                </p>
              </div>

              {/* Pillar 3 */}
              <div className="rounded-2xl border border-border bg-background p-5 sm:p-6 text-center shadow-soft flex flex-col items-center">
                <div className="h-14 w-14 rounded-2xl bg-accent/10 text-accent flex items-center justify-center mb-3">
                  <FastForward className="h-7 w-7" strokeWidth={2} />
                </div>
                <h3 className="font-headings text-sm sm:text-base font-bold text-primary uppercase tracking-wide">
                  Accelerated
                </h3>
                <p className="mt-1 text-xs sm:text-sm font-semibold text-muted-foreground">
                  As Few as 1–2 Days
                </p>
              </div>

              {/* Pillar 4 */}
              <div className="rounded-2xl border border-border bg-background p-5 sm:p-6 text-center shadow-soft flex flex-col items-center">
                <div className="h-14 w-14 rounded-2xl bg-accent/10 text-accent flex items-center justify-center mb-3">
                  <Heart className="h-7 w-7" strokeWidth={2} />
                </div>
                <h3 className="font-headings text-sm sm:text-base font-bold text-primary uppercase tracking-wide">
                  A More Comfortable
                </h3>
                <p className="mt-1 text-xs sm:text-sm font-semibold text-muted-foreground">
                  Patient Experience
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* COMPARISON SECTION — AMPA ONE VS CONVENTIONAL TMS */}
        <section className="py-16 sm:py-24 bg-background">
          <div className="container-px mx-auto max-w-6xl px-4">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="text-xs sm:text-sm font-bold uppercase tracking-widest text-accent">
                The Ideal Choice
              </span>
              <h2 className="mt-3 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-4xl">
                Ampa One™ vs. Conventional TMS
              </h2>
              <p className="mt-3 text-base text-muted-foreground leading-relaxed">
                See why patients and clinicians choose the modern Ampa One
                system over traditional, multi-week equipment.
              </p>
            </div>

            <div className="grid gap-8 lg:grid-cols-2 items-stretch">
              {/* Ampa One Card (The Ideal Choice) */}
              <div className="relative rounded-3xl border-2 border-accent/40 bg-gradient-to-b from-accent/5 via-card to-card p-6 sm:p-8 shadow-premium flex flex-col">
                <div className="absolute -top-3.5 left-8 rounded-full bg-gradient-accent px-4 py-1 text-xs font-bold uppercase tracking-wider text-accent-foreground shadow-md">
                  The Ideal Choice
                </div>

                <div className="flex items-center justify-between pb-6 border-b border-border mt-2">
                  <div>
                    <h3 className="font-headings text-2xl font-extrabold text-primary">
                      Ampa One™
                    </h3>
                    <p className="text-xs sm:text-sm text-accent font-semibold">
                      A Clearer Path Forward
                    </p>
                  </div>
                  <span className="rounded-full bg-accent/10 text-accent px-3 py-1 text-xs font-bold border border-accent/30">
                    Modern Innovation
                  </span>
                </div>

                <ul className="mt-6 space-y-4 flex-1">
                  {[
                    "Lightweight, handheld coil designed for comfort",
                    "Camera-guided targeting at every single session",
                    "Accelerated treatment — as few as 1–2 days",
                    "Private, comfortable suites with dedicated care team",
                    "Designed for an elevated patient experience",
                    "No surgery, no anesthesia, and no daily pill",
                  ].map((benefit) => (
                    <li key={benefit} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                      <span className="text-sm sm:text-base font-medium text-foreground">
                        {benefit}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-center gap-3 justify-between">
                  <div className="text-xs text-muted-foreground">
                    Available at Coral Springs clinic
                  </div>
                  <Button
                    asChild
                    className="w-full sm:w-auto bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90 font-bold"
                  >
                    <a href="/#consultation">Choose Ampa One</a>
                  </Button>
                </div>
              </div>

              {/* Conventional TMS Card */}
              <div className="rounded-3xl border border-border bg-card/60 p-6 sm:p-8 shadow-soft flex flex-col">
                <div className="flex items-center justify-between pb-6 border-b border-border">
                  <div>
                    <h3 className="font-headings text-2xl font-bold text-muted-foreground">
                      Conventional TMS
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground/80">
                      Standard Multi-Week Regimen
                    </p>
                  </div>
                  <span className="rounded-full bg-muted text-muted-foreground px-3 py-1 text-xs font-medium">
                    Legacy Standard
                  </span>
                </div>

                <ul className="mt-6 space-y-4 flex-1">
                  {[
                    "Heavy coil mounted on a bulky mechanical arm",
                    "Manual head measurements without live optical tracking",
                    "One session a day stretched across 4–6 weeks (30–36 visits)",
                    "Often performed in busy hospital or generic clinical settings",
                    "Standard technology with longer logistical burdens",
                    "More total time away from work, family, and commitments",
                    "Can be less comfortable during prolonged sessions",
                  ].map((drawback) => (
                    <li key={drawback} className="flex items-start gap-3">
                      <XCircle className="h-5 w-5 text-muted-foreground/60 shrink-0 mt-0.5" />
                      <span className="text-sm sm:text-base text-muted-foreground">
                        {drawback}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 pt-6 border-t border-border flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    Compare your options with our clinical director
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* GETTING STARTED — 4 STEPS (From the Flyer) */}
        <section className="py-16 sm:py-20 bg-muted/30 border-y border-border">
          <div className="container-px mx-auto max-w-6xl px-4">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="text-xs sm:text-sm font-bold uppercase tracking-widest text-accent">
                Getting Started
              </span>
              <h2 className="mt-3 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-4xl">
                Your 4-Step Treatment Journey
              </h2>
              <p className="mt-3 text-base text-muted-foreground">
                How our Coral Springs team guides you from your first question
                to lasting relief.
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {/* Step 1 */}
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft flex flex-col justify-between">
                <div>
                  <div className="h-12 w-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center font-headings font-bold text-lg mb-4">
                    1
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                    Step 01
                  </span>
                  <h3 className="mt-1 font-headings text-lg font-bold text-primary">
                    Consultation
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    Meet with our clinical team to review your medical history
                    and see if Ampa One is the right fit for your symptoms.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft flex flex-col justify-between">
                <div>
                  <div className="h-12 w-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center font-headings font-bold text-lg mb-4">
                    2
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                    Step 02
                  </span>
                  <h3 className="mt-1 font-headings text-lg font-bold text-primary">
                    Personalized Plan
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    We create a personalized accelerated schedule matched to
                    your life and commitments.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft flex flex-col justify-between">
                <div>
                  <div className="h-12 w-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center font-headings font-bold text-lg mb-4">
                    3
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                    Step 03
                  </span>
                  <h3 className="mt-1 font-headings text-lg font-bold text-primary">
                    Your Experience
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    Relax in our private suites with a dedicated care team,
                    quiet work desks, refreshments, and companion seating.
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft flex flex-col justify-between">
                <div>
                  <div className="h-12 w-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center font-headings font-bold text-lg mb-4">
                    4
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                    Step 04
                  </span>
                  <h3 className="mt-1 font-headings text-lg font-bold text-primary">
                    A Brighter Tomorrow
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    Reclaim your mood, clarity, and energy — and get back to
                    what matters most to you and your family.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FLYER DOWNLOAD & PRINT CALLOUT SECTION */}
        <section className="py-16 sm:py-20 bg-background">
          <div className="container-px mx-auto max-w-5xl px-4">
            <div className="rounded-3xl border-2 border-accent/30 bg-gradient-to-br from-card via-card to-accent/5 p-8 sm:p-12 shadow-premium">
              <div className="grid gap-8 lg:grid-cols-12 items-center">
                <div className="lg:col-span-4 flex justify-center">
                  <div
                    className="relative cursor-pointer group max-w-[260px] rounded-2xl overflow-hidden border-2 border-border shadow-lg"
                    onClick={() => setFlyerModalOpen(true)}
                  >
                    <img
                      src={AMPA_FLYER_IMAGE}
                      alt="Ampa One Patient Information Sheet"
                      className="w-full h-auto transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-primary-deep/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="rounded-full bg-white text-primary px-3 py-1.5 text-xs font-bold shadow">
                        Click to View
                      </span>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-8 space-y-4">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-xs font-semibold text-accent uppercase tracking-wider">
                    <FileDown className="h-3.5 w-3.5" /> Printable Patient
                    Information
                  </span>
                  <h3 className="font-headings text-2xl sm:text-3xl font-bold text-primary">
                    Download or Print the Ampa One™ Patient Guide
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    Take this concise, one-page guide with you to your next
                    doctor’s visit, share it with a spouse or family member, or
                    review the accelerated schedule and technology at your own
                    pace.
                  </p>

                  <div className="flex flex-wrap items-center gap-3 pt-2">
                    <Button
                      type="button"
                      size="lg"
                      onClick={handlePrint}
                      className="bg-primary text-white hover:bg-primary/90 font-bold gap-2"
                    >
                      <Printer className="h-4 w-4" /> Print Flyer
                    </Button>
                    <Button
                      asChild
                      size="lg"
                      className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90 font-bold gap-2"
                    >
                      <a
                        href={AMPA_FLYER_IMAGE}
                        download="Ampa-One-Depression-Treatment-Flyer.png"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FileDown className="h-4 w-4" /> Download High-Res Flyer
                      </a>
                    </Button>
                    <Button
                      type="button"
                      size="lg"
                      variant="outline"
                      onClick={() => setFlyerModalOpen(true)}
                      className="border-border text-foreground hover:bg-muted font-semibold gap-2"
                    >
                      <Maximize2 className="h-4 w-4" /> View Fullscreen
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CLINICAL DISCLAIMER (From Flyer Footer) */}
        <section className="py-8 bg-muted/40 border-t border-border">
          <div className="container-px mx-auto max-w-4xl px-4 text-center">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Ampa One is FDA-cleared for major depressive disorder in adults
              who have not improved on antidepressant medication. Individual
              results may vary. Consultation required. Ampa One is manufactured
              by Ampa Inc.; Neuro Wellness TMS Centers of America is an
              independent provider.
            </p>
          </div>
        </section>

        {/* RELATED PAGES & BOTTOM CONSULTATION */}
        <RelatedPages
          heading="Explore Related Treatments & Resources"
          links={[
            ...allTreatmentsLinks.filter((l) => l.href !== "/ampa"),
            ...allServiceLinks,
            ...allDecisionLinks,
          ]}
        />
        <Consultation />
      </main>

      {/* LIGHTBOX MODAL FOR FULLSCREEN FLYER VIEW & PRINT */}
      {flyerModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm animate-fade-in"
          onClick={() => setFlyerModalOpen(false)}
        >
          <div
            className="relative max-w-2xl w-full max-h-[92vh] overflow-y-auto rounded-2xl bg-card border border-border p-4 shadow-2xl space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-2 border-b border-border">
              <div className="flex items-center gap-2">
                <FileDown className="h-5 w-5 text-accent" />
                <h4 className="font-headings text-base font-bold text-primary">
                  Ampa One™ Patient Guide
                </h4>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handlePrint}
                  className="gap-1.5 text-xs font-semibold"
                >
                  <Printer className="h-3.5 w-3.5" /> Print
                </Button>
                <Button
                  size="sm"
                  asChild
                  className="bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5 text-xs font-semibold"
                >
                  <a
                    href={AMPA_FLYER_IMAGE}
                    download="Ampa-One-Depression-Treatment-Flyer.png"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FileDown className="h-3.5 w-3.5" /> Save
                  </a>
                </Button>
                <button
                  onClick={() => setFlyerModalOpen(false)}
                  className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Close flyer preview"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex justify-center bg-muted/30 p-2 rounded-xl">
              <img
                src={AMPA_FLYER_IMAGE}
                alt="Ampa One Patient Educational Flyer"
                className="max-h-[75vh] w-auto object-contain rounded-lg shadow-md"
              />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default OneDayTmsPage;
